// Copyright 2020 Sorama B.V.

import { DialogService } from "aurelia-dialog";
import { autoinject, computedFrom, transient } from "aurelia-framework";
import { FileToUpload, FileUploadError, FileUploadStatus } from "controls/file-dropbox/FileDropbox";
import { As2ImportResult, GenericMeasurement } from "datatypes/SoramaWebApi";
import { NotSupportedError } from "errors/NotSupportedError";
import { FileNameExtensions } from "extensions/FileNameExtensions";
import { parseSorXFile } from "helpers/SorxHelpers";
import { IModal } from "libraries/WorkflowFramework/Interfaces/IModal";
import { MeasurementRepository } from "repositories/MeasurementRepository";
import { MeasurementCollectionService } from "services/Measurement/MeasurementCollectionService";
import { MeasurementStorageService } from "services/Measurement/MeasurementStorageService";
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import { PrpdDataService } from "services/ReportEx/PrpdDataService";

@transient()
@autoinject
export class FileImporterModal implements IModal {
    protected importedFiles: As2ImportResult[] = [];
    protected importedGenericMeasurementFiles: GenericMeasurement[] = [];

    private readonly maxFileSizeAs2 = 409600000;
    private readonly maxFileSizeSor = 1073741824;
    private readonly uploadFileDescription = false;
    private readonly as2FileNameExtension = "as2";
    private readonly sorFileExtension = "sor";
    private readonly sorXFileExtension = "sorx";
    private readonly specialMeasurementExtension = "json";
    protected successMessage: string;

    protected importFinished = false;

    @computedFrom("importedFiles.length")
    protected get hasLinkedMediaObjects(): boolean {
        return this.importedFiles.some((file) => file.linkedMediaObjects);
    }

    constructor(
        private readonly measurementCollectionService: MeasurementCollectionService,
        private readonly projectManagementService: IProjectManagementService,
        private readonly dialogService: DialogService,
        private readonly measurementStorageService: MeasurementStorageService,
        private readonly measurementRepository: MeasurementRepository,
        private readonly prpdDataService: PrpdDataService
    ) {}

    public activateModal(state: void) {}

    protected upload = async (file: FileToUpload): Promise<void> => {
        this.successMessage = "";
        const projectId = this.projectManagementService.currentProject.item.id;
        const fileType = FileNameExtensions.getExtension(file.name);

        this.validateFileToUpload(file, projectId);

        switch (fileType) {
            case this.specialMeasurementExtension:
                await this.uploadSpecialMeasurement(file, projectId);
                break;
            case this.as2FileNameExtension:
                throw new NotSupportedError(
                    "Uploading .as2 files is no longer supported. Please contact Sorama if you still need this functionality."
                );
                // SUS-1537: Remove .as2 support (legacy path retained)
                // await this.uploadAs2File(file, projectId);
                break;
            case this.sorFileExtension:
                await this.importSorFile(file, projectId);
                break;
            case this.sorXFileExtension:
                await this.importSorXFile(file, projectId);
                break;
            default:
                throw new NotSupportedError(`Uploading files with the extension of ${file.name} is not supported.`);
        }
        return;
    };

    protected async importSorXFile(file: FileToUpload, projectId: string): Promise<void> {
        // Validate the file by parsing it
        try {
            await parseSorXFile(file, (buffer: ArrayBuffer) => this.prpdDataService.parsePrpdBin(buffer));
        } catch (e) {
            file.errorMessage = "Failed to parse SorX archive";
            file.status = FileUploadStatus.Error;
            throw e;
        }

        // Upload the sorx file directly to cloud (encrypted)
        try {
            const genericMeasurement = await this.measurementStorageService.uploadSorxToCloud(
                file,
                projectId,
                file.progressCallback
            );
            this.importedGenericMeasurementFiles.push(genericMeasurement);
            this.projectManagementService.refreshMeasurements();
        } catch (e) {
            file.errorMessage = (e as Error).message || "Failed to import SorX file";
            file.status = FileUploadStatus.Error;
            throw e;
        }

        file.status = FileUploadStatus.Success;
    }

    protected validateFileToUpload(file: FileToUpload, projectId: string): void {
        const fileName = FileNameExtensions.getName(file.name).trim();

        if (file.name == undefined || !fileName) {
            throw new Error("File name cannot be empty.");
        }

        // Prevent duplicate measurement names (case-insensitive)
        const isAlreadyImported = this.measurementCollectionService.measurements.some(
            (m) => m.canAnalyse && m.projectId === projectId && m.name?.trim() === fileName
        );

        if (isAlreadyImported) {
            throw new Error(`A measurement with the name "${file.name}" has already been imported.`);
        }
    }

    protected async importSorFile(file: FileToUpload, projectId: string): Promise<void> {
        try {
            const ownerId = this.projectManagementService.currentProject.item.ownerId;
            const genericMeasurement = await this.measurementStorageService.saveSorFileLocallyAndRegister(
                file,
                projectId,
                ownerId,
                file.progressCallback
            );
            this.importedGenericMeasurementFiles.push(genericMeasurement);
            this.projectManagementService.refreshMeasurements();
            file.status = FileUploadStatus.Success;
        } catch (e) {
            file.errorMessage = (e as Error).message || "Failed to import .sor file";
            file.status = FileUploadStatus.Error;
            throw e;
        }
    }

    protected uploadAs2File = async (file: FileToUpload, projectId: string): Promise<As2ImportResult> => {
        try {
            const x = await this.measurementRepository.uploadAs2MeasurementToCloud(
                file.data,
                projectId,
                file.name,
                file.progressCallback
            );
            this.importedFiles.push(x);
            this.projectManagementService.refreshMeasurements();
            file.status = FileUploadStatus.Success;
            return x;
        } catch (e) {
            file.errorMessage = (e as Error).message || "Failed to upload .as2 file";
            file.status = FileUploadStatus.Error;
            throw e;
        }
    };

    protected uploadSpecialMeasurement = (file: FileToUpload, projectId: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = (): void => {
                try {
                    const data = JSON.parse(<string>reader.result);
                    data.fileName = FileNameExtensions.getName(file.name);
                    if (!data.name) {
                        data.name = data.fileName;
                    }
                    this.measurementRepository
                        .createSpecialMeasurement(data, projectId, file.progressCallback)
                        .then((x) => {
                            this.importedGenericMeasurementFiles.push(x);
                            file.status = FileUploadStatus.Success;
                            resolve();
                        })
                        .catch((error) => {
                            file.errorMessage = error.message || "Failed to import special measurement";
                            file.status = FileUploadStatus.Error;
                            reject(error);
                        });
                } catch (e) {
                    file.errorMessage = (e as Error).message || "Invalid JSON structure";
                    file.status = FileUploadStatus.Error;
                    reject(e);
                }
            };
            reader.onerror = () => {
                file.errorMessage = reader.error?.message || "Failed to read file";
                file.status = FileUploadStatus.Error;
                reject(reader.error);
            };

            reader.readAsText(file.data);
        });
    };

    protected resetUploadState = (): void => {
        this.importedFiles = [];
        this.importedGenericMeasurementFiles = [];
        this.importFinished = false;
    };

    protected handleUploadFinished = (failedImports: FileUploadError[]): void => {
        this.importFinished = true;
        this.measurementCollectionService.refreshAllMeasurements();
    };
}
